export const HARVEST_LOSS_SAMPLE_AREA_M2 = 0.25;
export const HARVEST_LOSS_SAMPLE_WIDTH_CM = 50;
export const HARVEST_LOSS_SAMPLE_LENGTH_CM = 50;
export const HARVEST_LOSS_SEEDS_PER_HA_FACTOR = 10000 / HARVEST_LOSS_SAMPLE_AREA_M2;
export const DEFAULT_KG_PER_BAG = 60;
export const DEFAULT_MIN_REPETITIONS = 3;
export const DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT = 50;
export const HARVEST_LOSS_EVENT_TYPE = "PERDA_COLHEITA_AMOSTRA";
export const HARVEST_LOSS_MAP_FOCUS_WINDOW_MS = 30 * 60 * 1000;

function firstDefined() {
  for (let index = 0; index < arguments.length; index += 1) {
    const value = arguments[index];
    if (value !== undefined && value !== null) {
      return value;
    }
  }
  return undefined;
}

function averageOf(values) {
  const clean = values.filter((v) => v != null && Number.isFinite(v));
  if (!clean.length) return null;
  return clean.reduce((sum, v) => sum + v, 0) / clean.length;
}

export function parseHarvestLossPlan(plan) {
  const raw = plan && plan.harvest_loss_monitoring;
  if (!raw || typeof raw !== "object") return null;

  const pmsG = Number(raw.pms_g);
  const toleratedLossKgHa = Number(raw.tolerated_loss_kg_ha);
  const kgPerBag = Number(raw.kg_per_bag) || DEFAULT_KG_PER_BAG;
  const minRepetitions = Number(raw.min_repetitions) || DEFAULT_MIN_REPETITIONS;

  if (!Number.isFinite(pmsG) || pmsG <= 0) return null;

  return {
    pmsG,
    toleratedLossKgHa: Number.isFinite(toleratedLossKgHa) ? toleratedLossKgHa : null,
    kgPerBag,
    minRepetitions,
    sampleAreaM2: Number(raw.sample_area_m2) || HARVEST_LOSS_SAMPLE_AREA_M2,
    sampleWidthCm: Number(raw.sample_width_cm) || HARVEST_LOSS_SAMPLE_WIDTH_CM,
    sampleLengthCm: Number(raw.sample_length_cm) || HARVEST_LOSS_SAMPLE_LENGTH_CM,
    alertThresholdPercent:
      Number(raw.alert_threshold_percent) || DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT,
    instructions: String(raw.instructions || "").trim(),
  };
}

export function computeHarvestLossFromSeedCount({
  seedCount,
  pmsG,
  kgPerBag = DEFAULT_KG_PER_BAG,
}) {
  const count = Number(seedCount);
  const pms = Number(pmsG);
  const bagKg = Number(kgPerBag) || DEFAULT_KG_PER_BAG;

  if (!Number.isFinite(count) || count < 0 || !Number.isFinite(pms) || pms <= 0) {
    return {
      seedsPerHa: null,
      kgPerHa: null,
      bagsPerHa: null,
    };
  }

  const seedsPerHa = count * HARVEST_LOSS_SEEDS_PER_HA_FACTOR;
  const kgPerHa = (seedsPerHa * pms) / 1000000;
  const bagsPerHa = bagKg > 0 ? kgPerHa / bagKg : null;

  return { seedsPerHa, kgPerHa, bagsPerHa };
}

export function formatHarvestLossRate(value, unit, digits = 2) {
  if (value == null || !Number.isFinite(value)) return "—";
  return `${value.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })} ${unit}`;
}

function metricsFromSample(sample) {
  if (!sample) return null;
  return {
    seedCount: sample.seedCount,
    kgPerHa: sample.kgPerHa,
    bagsPerHa: sample.bagsPerHa,
    timestamp: sample.timestamp,
  };
}

function averageMetrics(samples, field) {
  return averageOf(samples.map((sample) => sample[field]));
}

export function harvestLossMachineKey(sample) {
  const name = String((sample && sample.machineName) || "").trim();
  return name || "__sem_maquina__";
}

export function harvestLossMachineLabel(machineKey, t) {
  if (!machineKey || machineKey === "__sem_maquina__") {
    return t("Sem máquina");
  }
  return machineKey;
}

export function groupHarvestLossSamplesByMachine(samples) {
  const map = new Map();

  (samples || []).forEach((sample) => {
    if (!sample) return;
    const machineKey = harvestLossMachineKey(sample);
    if (!map.has(machineKey)) {
      map.set(machineKey, {
        machineKey,
        machineName: machineKey === "__sem_maquina__" ? "" : machineKey,
        samples: [],
      });
    }
    map.get(machineKey).samples.push(sample);
  });

  return Array.from(map.values())
    .map((group) => ({
      ...group,
      samples: group.samples.slice().sort((a, b) => {
        const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return ta - tb;
      }),
    }))
    .sort((a, b) => {
      const ta = a.samples[0] && a.samples[0].timestamp
        ? new Date(a.samples[0].timestamp).getTime()
        : 0;
      const tb = b.samples[0] && b.samples[0].timestamp
        ? new Date(b.samples[0].timestamp).getTime()
        : 0;
      return ta - tb;
    });
}

export function summarizeHarvestLossSamples(samples) {
  const ordered = [...(samples || [])]
    .filter((sample) => sample && Number.isFinite(sample.kgPerHa))
    .sort((a, b) => {
      const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return ta - tb;
    });

  if (!ordered.length) {
    return {
      sampleCount: 0,
      lastSample: null,
      avgLast3: null,
      avgTotal: null,
    };
  }

  const lastSample = metricsFromSample(ordered[ordered.length - 1]);
  const rollingWindow = Math.min(3, ordered.length);
  const rollingSamples = ordered.slice(-rollingWindow);

  return {
    sampleCount: ordered.length,
    rollingWindow,
    lastSample,
    avgLast3: {
      seedCount: averageMetrics(rollingSamples, "seedCount"),
      kgPerHa: averageMetrics(rollingSamples, "kgPerHa"),
      bagsPerHa: averageMetrics(rollingSamples, "bagsPerHa"),
    },
    avgTotal: {
      seedCount: averageMetrics(ordered, "seedCount"),
      kgPerHa: averageMetrics(ordered, "kgPerHa"),
      bagsPerHa: averageMetrics(ordered, "bagsPerHa"),
    },
  };
}

export function summarizeHarvestLossSamplesByMachine(samples) {
  const machineGroups = groupHarvestLossSamplesByMachine(samples);
  const machines = machineGroups.map((group) => ({
    machineKey: group.machineKey,
    machineName: group.machineName,
    samples: group.samples,
    summary: summarizeHarvestLossSamples(group.samples),
  }));

  return {
    machines,
    overall: summarizeHarvestLossSamples(samples),
    sampleCount: machines.reduce(
      (total, machine) => total + ((machine.summary && machine.summary.sampleCount) || 0),
      0
    ),
  };
}

export function harvestLossSampleFromSyncRecord(record) {
  if (!record) return null;

  const payload =
    record.payloadParsed ||
    record.payloadData ||
    record.payload_data ||
    record.payload ||
    {};

  const eventType =
    record.event_type || payload.tipo_evento || payload.event_type || "";

  if (eventType !== HARVEST_LOSS_EVENT_TYPE) return null;

  const seedCount = Number(
    firstDefined(
      payload.sementes_contadas,
      payload.seed_count,
      payload.contagem_sementes
    )
  );
  const pmsG = Number(firstDefined(payload.pms_g, payload.pms));
  const kgPerHa = Number(firstDefined(payload.perda_kg_ha, payload.kg_per_ha));
  const bagsPerHa = Number(firstDefined(payload.perda_sacas_ha, payload.bags_per_ha));
  const serviceOrderId = Number(
    firstDefined(payload.ordem_servico_id, payload.service_order_id)
  );

  if (!Number.isFinite(seedCount) || seedCount < 0) return null;

  const computed =
    Number.isFinite(kgPerHa) && kgPerHa >= 0
      ? { kgPerHa, bagsPerHa: Number.isFinite(bagsPerHa) ? bagsPerHa : null }
      : computeHarvestLossFromSeedCount({
          seedCount,
          pmsG,
          kgPerBag: firstDefined(payload.kg_por_saca, payload.kg_per_bag),
        });

  const locationRaw = payload.localizacao || payload.location || {};
  const latitude = Number(
    firstDefined(locationRaw.latitude, payload.latitude, record.latitude)
  );
  const longitude = Number(
    firstDefined(locationRaw.longitude, payload.longitude, record.longitude)
  );

  return {
    uuid: record.uuid,
    serviceOrderId: Number.isFinite(serviceOrderId) ? serviceOrderId : null,
    repetitionNumber:
      Number(firstDefined(payload.repeticao, payload.repetition_number)) || null,
    seedCount,
    pmsG: Number.isFinite(pmsG) ? pmsG : null,
    kgPerHa: computed.kgPerHa,
    bagsPerHa: computed.bagsPerHa,
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null,
    notes: String(firstDefined(payload.observacoes, payload.notes) || "").trim(),
    machineName: String(
      firstDefined(
        payload.nome_maquina_colheita,
        payload.maquina_colheita,
        payload.nome_maquina
      ) || ""
    ).trim(),
    timestamp:
      record.received_at ||
      record.timestamp ||
      payload.timestamp_utc ||
      payload.registrado_em_utc,
    operatorId:
      record.operatorId != null
        ? Number(record.operatorId)
        : record.operator_id != null
          ? Number(record.operator_id)
          : null,
    operatorName: record.operator_name || record.operatorName || payload.nome_operador || "",
  };
}

export function buildHarvestLossSampleMapView(sample, t) {
  if (!sample || !sample.timestamp) return null;

  const timestamp = new Date(sample.timestamp);
  if (Number.isNaN(timestamp.getTime())) return null;

  const centerMs = timestamp.getTime();
  const dayKey = timestamp.toISOString().slice(0, 10);
  const focusStart = new Date(
    centerMs - HARVEST_LOSS_MAP_FOCUS_WINDOW_MS
  ).toISOString();
  const focusEnd = new Date(
    centerMs + HARVEST_LOSS_MAP_FOCUS_WINDOW_MS
  ).toISOString();

  const machineLabel = sample.machineName || t("colheita");
  const repetitionLabel =
    sample.repetitionNumber != null ? String(sample.repetitionNumber) : "—";

  return {
    dayKey,
    focusStart,
    focusEnd,
    mapFullDay: true,
    label: t("Amostra {{repetition}} — {{machine}}", {
      repetition: repetitionLabel,
      machine: machineLabel,
    }),
    mapCenter:
      sample.latitude != null && sample.longitude != null
        ? { lat: Number(sample.latitude), lng: Number(sample.longitude) }
        : null,
    operatorIds:
      sample.operatorId != null && Number.isFinite(Number(sample.operatorId))
        ? [Number(sample.operatorId)]
        : [],
    token: Date.now(),
  };
}

export function groupHarvestLossSamplesByOrder(samples) {
  const groups = new Map();

  (samples || []).forEach((sample) => {
    const key = sample.serviceOrderId != null ? String(sample.serviceOrderId) : "sem-ordem";
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(sample);
  });

  return groups;
}

export function computeHarvestLossAlertLimit(
  toleratedLossKgHa,
  alertThresholdPercent = DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT
) {
  const tolerated = Number(toleratedLossKgHa);
  const threshold = Number(alertThresholdPercent);
  if (!Number.isFinite(tolerated) || tolerated <= 0) return null;
  const pct = Number.isFinite(threshold) && threshold >= 0
    ? threshold
    : DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT;
  return tolerated * (1 + pct / 100);
}

export function toleranceStatus(kgPerHa, toleratedLossKgHa, alertThresholdPercent) {
  if (kgPerHa == null || !Number.isFinite(kgPerHa)) return null;
  if (toleratedLossKgHa == null || !Number.isFinite(toleratedLossKgHa)) {
    return "unknown";
  }
  const alertLimit = computeHarvestLossAlertLimit(
    toleratedLossKgHa,
    alertThresholdPercent
  );
  if (alertLimit != null && kgPerHa > alertLimit) return "alert";
  if (kgPerHa <= toleratedLossKgHa) return "ok";
  return "above";
}
